import React, { useContext, useMemo } from "react";
import Menu from "./components/menu/Menu";
import StatusBar from "./components/status-bar/StatusBar";
import { EMenuItems, EMenuItemTypes, TMenuSpacer, TMenuTextAndIconItem } from "./components/menu/menu-items";
// import * as Icons from 'react-bootstrap-icons';
import * as Icons from "react-icons/bs";
import { TabbedPanel, Target } from "./components/shared/Panels";
import { CACHE_KEYS, useCache } from "./hooks/useCache";
import { LoadingSplashPage } from "./LoadingSplashPage";
import { TDocumentTree, Themes } from "./types";
import { SideBarTabs, useDocuments } from "./contexts/DekstopContext";
import useResizableSidebar from "./hooks/useResizeableSidebar";
import MinimizedWindowsBar from "./components/window-management/MinimizedWindowsBar";
import VirtualWindowsContainer from "./components/window-management/VirtualWindowsContainer";
import { UserContext } from "../context";
import { ChooseTheme } from "./ChooseTheme";
import { newApiService } from "./services/api-service";

import "./scss/base.scss";
import "./DekstopMainPage.scss";
import "./scss/theme-modern.scss";
import "./scss/theme-vintage.scss";

const DesktopMainPage = () => {
  // const [theme, setTheme] = useState(Themes.VINTAGE);

  const { loggedInUser, updateLoggedInUser } = useContext(UserContext);
  const theme = useMemo<Themes | undefined>(() => (loggedInUser as any)?.theme, [loggedInUser]); // TODO - move this to the user context

  const { sidebarWidth, handleMouseDown } = useResizableSidebar({
    initialWidth: 250,
    minWidth: 100,
    maxWidth: 500,
  });

  const {
    cache,
    showDocumentControls,
    showDocumentControlsText,
    showStatusBar,
    windows,
    minimizedWindows,
    menuItems,
    searchResults,
    hasFocusedWindow,
    hasAnyFocusedWindow,
    isFocusedWindowInFavorites,
    closeWindow,
    minimizeWindow,
    minimizeVisibleMaximizedWindow,
    restoreVisibleMaximizedWindow,
    closeVisibleMaximizedWindow,
    maximizeWindow,
    restoreWindow,
    bringToFront,
    updateWindowPosition,
    updateWindowSize,
    showAdvancedSearch,
    showInDocumentSearch,
    printFocusedWindow,
    printSelection,
    exportFocusedWindowDocx,
    exportFocusedWindowPDF,
    showFocusedWindowsDocumentRels,
    showFocusedWindowsDocumentHistory,
    toggleFocusedWindowFavorites,
    focusedWindowNext,
    focusedWindowPrevious,
  } = useDocuments();

  // Handler for creating a new document (placeholder)
  const handleNewDocument = () => {
    alert("New document functionality would be implemented here");
  };

  // Handler for refreshing the document list
  const handleRefresh = () => {
    window.location.reload();
  };

  const docMenuItems: (TMenuTextAndIconItem | TMenuSpacer)[] = useMemo(() => [{
    type: EMenuItemTypes.TEXT_ICON,
    name: EMenuItems.SEARCH,
    contents: [],
    disabled: false,
    label: 'Cautare',
    icon: <Icons.BsBinocularsFill className='md' />,
    onClick: () => showAdvancedSearch(),
  }, {
    type: EMenuItemTypes.TEXT_ICON,
    name: EMenuItems.PRINT,
    contents: [],
    disabled: !hasAnyFocusedWindow,
    label: 'Tiparire',
    icon: <Icons.BsPrinterFill className='md' />,
    onClick: () => printFocusedWindow(),
  }, {
    type: EMenuItemTypes.TEXT_ICON,
    name: EMenuItems.PRINT_SELECTION,
    contents: [],
    disabled: !hasAnyFocusedWindow,
    label: 'Selectie',
    icon: <Icons.BsPrinterFill className='md' />,
    onClick: () => printSelection(),
  }, {
    type: EMenuItemTypes.TEXT_ICON,
    name: EMenuItems.EXPORT_DOCX,
    contents: [],
    disabled: !hasAnyFocusedWindow,
    label: 'Word',
    icon: <Icons.BsFileEarmarkWord className='md' />,
    onClick: () => exportFocusedWindowDocx(),
  }, {
    type: EMenuItemTypes.TEXT_ICON,
    name: EMenuItems.EXPORT_PDF,
    contents: [],
    disabled: !hasAnyFocusedWindow,
    label: 'PDF',
    icon: <Icons.BsFileEarmarkPdf className='md' />,
    onClick: () => exportFocusedWindowPDF(),
  }, {
    type: EMenuItemTypes.TEXT_ICON,
    name: EMenuItems.LINKS,
    contents: [],
    disabled: !hasFocusedWindow,
    label: 'Relatii',
    icon: <Icons.BsLink className='md' />,
    onClick: () => showFocusedWindowsDocumentRels(),
  }, {
    type: EMenuItemTypes.TEXT_ICON,
    name: EMenuItems.HISTORY,
    contents: [],
    disabled: !hasFocusedWindow,
    label: 'Istoric',
    icon: <Icons.BsClockHistory className='md' />,
    onClick: () => showFocusedWindowsDocumentHistory(),
  }, {
    type: EMenuItemTypes.SPACER,
    orientation: 'row',
  }, {
    type: EMenuItemTypes.TEXT_ICON,
    name: EMenuItems.BOOKMARKS,
    contents: [],
    disabled: !hasFocusedWindow,
    label: 'Favorite',
    icon: isFocusedWindowInFavorites
      ? <Icons.BsStarFill className='md' />
      : <Icons.BsStar className='md' />,
    onClick: () => toggleFocusedWindowFavorites(),
  }, {
    type: EMenuItemTypes.TEXT_ICON,
    name: EMenuItems.WORDS,
    contents: [],
    disabled: !hasFocusedWindow,
    label: 'Cuvinte',
    icon: <Icons.BsBinocularsFill className='md' />,
    onClick: () => showInDocumentSearch(),
  }], [toggleFocusedWindowFavorites, hasAnyFocusedWindow, hasFocusedWindow, printFocusedWindow, printSelection, showAdvancedSearch, showInDocumentSearch, showFocusedWindowsDocumentRels, showFocusedWindowsDocumentHistory, isFocusedWindowInFavorites])

  const target = useMemo(() => (theme === Themes.VINTAGE ? Target.DESKTOP_VINTAGE : Target.DESKTOP_MODERN), [theme]);

  const tabData = useMemo(() => {
    if (cache.isLoading) return [];
    return [
      {
        name: SideBarTabs.INDEX,
        label: "Index",
        data: cache.get(CACHE_KEYS.INDEX_BY_YEAR).data as TDocumentTree,
        fetcher: newApiService.loadDocTitlesForYearAndType,
      },
      {
        name: SideBarTabs.SEARCH,
        label: "Rezultat",
        data: searchResults,
      },
      {
        name: SideBarTabs.BOOKMARKS,
        label: "Favorite",
        data: cache.get(CACHE_KEYS.BOOKMARKS).data as TDocumentTree,
      },
      {
        name: SideBarTabs.MONITORS,
        label: "Monitoare",
        data: cache.get(CACHE_KEYS.INDEX_BY_MONITOR).data as TDocumentTree,
        fetcher: newApiService.loadDocTitlesForYearAndMon,
      },
      {
        name: SideBarTabs.READ_LATER,
        label: "Acte de citit",
        data: cache.get(CACHE_KEYS.READ_LATER).data as TDocumentTree,
      },
    ]
  }, [cache, searchResults])

  if (cache.isLoading) return <LoadingSplashPage />;

  if (loggedInUser && !theme)
    return (
      <ChooseTheme
        onThemeChange={async (theme) => {
          await newApiService.updateUserTheme(theme);
          updateLoggedInUser({ ...(loggedInUser as any), theme });
        }}
      />
    );
  return (
    <div className={`main-page desktop ${theme}`}>
      <Menu
        showBottomSeparator={theme === Themes.VINTAGE}
        pre={
          windows.length > 0
            ? [
              {
                type: EMenuItemTypes.ICON,
                icon: <Icons.BsFileEarmarkText />,
                name: EMenuItems.DOC_ACTIONS,
                contents: [],
              },
            ]
            : undefined
        }
        target={target}
        data={menuItems}
        post={
          windows.find((w) => w.isMaximized)
            ? [
              {
                type: EMenuItemTypes.ICON,
                icon: <>&#8211;</>,
                name: EMenuItems.DOC_ACTIONS,
                contents: [],
                onClick: () => minimizeVisibleMaximizedWindow(),
              },
              {
                type: EMenuItemTypes.ICON,
                icon: <>❐</>,
                name: EMenuItems.DOC_ACTIONS,
                contents: [],
                onClick: () => restoreVisibleMaximizedWindow(),
              },
              {
                type: EMenuItemTypes.ICON,
                icon: <>✕</>,
                name: EMenuItems.DOC_ACTIONS,
                contents: [],
                onClick: () => closeVisibleMaximizedWindow(),
              },
            ]
            : undefined
        }
      />
      <div className="main-content-container">
        <div className="sidebar" style={{ width: `${sidebarWidth}px` }}>
          <TabbedPanel
            data={tabData}
          />
        </div>
        <div className="resizer" onMouseDown={handleMouseDown} style={{ left: `${sidebarWidth}px` }} />
        <div
          className={`main-content ${showDocumentControls ? (showDocumentControlsText ? "with-full-menu" : "with-half-menu") : "full"}`}
          style={{ width: `calc(100% - ${sidebarWidth}px)` }}
        >
          <Menu data={docMenuItems} itemPadding="lg" />
          <div
            className={`main-content-inner ${showDocumentControls ? (showDocumentControlsText ? "with-full-menu" : "with-half-menu") : "full"
              }`}
          >
            {/* <WindowArrangementControls
                        onCascade={cascadeWindows}
                        onTileHorizontal={tileWindowsHorizontally}
                        onTileVertical={tileWindowsVertically}
                        onMinimizeAll={minimizeAllWindows}
                        onCloseAll={closeAllWindows}
                        windowCount={windows.length}
                    /> */}
            <VirtualWindowsContainer
              windows={windows}
              onClose={closeWindow}
              onMinimize={minimizeWindow}
              onMaximize={maximizeWindow}
              onBringToFront={bringToFront}
              onUpdatePosition={updateWindowPosition}
              onUpdateSize={updateWindowSize}
            />
            <MinimizedWindowsBar
              windows={windows}
              minimizedWindows={minimizedWindows}
              onFocus={bringToFront}
              onRestore={restoreWindow}
              onClose={closeWindow}
            />
          </div>
        </div>
      </div>
      {showStatusBar ? (
        <StatusBar openWindowCount={windows.length} minimizedWindowCount={minimizedWindows.length} />
      ) : null}
    </div>
    // <div className={`main-page desktop ${theme}`}>
    //     <Menu
    //         showBottomSeparator
    //         pre={
    //             hasOpenDocs
    //                 ? [{
    //                     type: EMenuItemTypes.ICON,
    //                     icon: <Icons.BsFileEarmarkText />,
    //                     name: EMenuItems.DOC_ACTIONS,
    //                     contents: [],
    //                 }]
    //                 : undefined
    //         }
    //         target={target}
    //         data={menuItems}
    //         post={
    //             hasOpenDocs
    //                 ? [{
    //                     type: EMenuItemTypes.ICON,
    //                     icon: <Icons.BsDash className='menubar-document-icon' />,
    //                     name: EMenuItems.DOC_ACTIONS,
    //                     contents: [],
    //                     onClick: () => minimizeWindow(),
    //                 },
    //                 {
    //                     type: EMenuItemTypes.ICON,
    //                     icon: <Icons.BsSquare className='menubar-document-icon small' />,
    //                     name: EMenuItems.DOC_ACTIONS,
    //                     contents: [],
    //                     onClick: () => restoreWindow()
    //                 },
    //                 {
    //                     type: EMenuItemTypes.ICON,
    //                     icon: <Icons.BsX className='menubar-document-icon' />,
    //                     name: EMenuItems.DOC_ACTIONS,
    //                     contents: [],
    //                     onClick: () => closeWindow(),
    //                 }]
    //                 : undefined
    //         }
    //     />
    //     <div className={`content desktop ${theme}`}>
    //         <div className={`overview-panel desktop ${theme}`}>
    //             <TabbedPanel
    //                 target={target}
    //                 data={[{
    //                     name: 'index',
    //                     label: 'Index',
    //                     content: <DocumentTree tree={cache.get(CACHE_KEYS.INDEX_BY_YEAR).data as TDocumentTree} fetcher={api.loadDocTitlesForYearAndType} />
    //                 }, {
    //                     name: 'search-results',
    //                     label: 'Rezultat',
    //                     content: <>TODO</>
    //                 }, {
    //                     name: 'bookmarked-docs',
    //                     label: 'Favorite',
    //                     content: <DocumentTree tree={cache.get(CACHE_KEYS.BOOKMARKS).data as TDocumentTree} />
    //                 }, {
    //                     name: 'monitors',
    //                     label: 'Monitoare',
    //                     content: <DocumentTree tree={cache.get(CACHE_KEYS.INDEX_BY_MONITOR).data as TDocumentTree} />
    //                 }, {
    //                     name: 'read-later-docs',
    //                     label: 'Acte de citit',
    //                     content: <DocumentTree tree={cache.get(CACHE_KEYS.READ_LATER).data as TDocumentTree} />
    //                 }]}
    //             />
    //         </div>
    //         <div className={`content-window desktop ${theme}`}>
    //             <Menu data={docMenuItems} target={target} itemPadding='lg' />
    //             <div className={`content-window-inner desktop ${theme}`}>
    //                 {
    //                     windows
    //                         .filter(w => w.state !== EWindowState.MINIMIZED)
    //                         .map((w, idx) =>
    //                             <DocumentWindow
    //                                 key={w.id}
    //                                 id={w.id}
    //                                 docid={w.docid}
    //                                 title={w.title}
    //                                 minimized={w.state === EWindowState.MINIMIZED}
    //                                 maximized={w.state === EWindowState.MAXIMIZED}
    //                                 active={true}
    //                                 onMinimize={() => minimizeWindow(w.id)}
    //                                 onRestore={() => restoreWindow(w.id)}
    //                                 onMaximize={() => maximizeWindow(w.id)}
    //                                 onFocus={() => focusWindow(w.id)}
    //                                 onClose={() => closeWindow(w.id)}
    //                             />
    //                         )
    //                 }
    //                 <div className='minimized-docs-wrapper'>
    //                     {
    //                         windows
    //                             .filter(w => w.state === EWindowState.MINIMIZED)
    //                             .map((w, idx) =>
    //                                 <DocumentWindow
    //                                     key={w.id}
    //                                     id={w.id}
    //                                     docid={w.docid}
    //                                     title={w.title}
    //                                     minimized={w.state === EWindowState.MINIMIZED}
    //                                     maximized={w.state === EWindowState.MAXIMIZED}
    //                                     active={true} // TODO
    //                                     onMinimize={() => minimizeWindow(w.id)}
    //                                     onRestore={() => restoreWindow(w.id)}
    //                                     onMaximize={() => maximizeWindow(w.id)}
    //                                     onFocus={() => focusWindow(w.id)}
    //                                     onClose={() => closeWindow(w.id)}
    //                                 />
    //                             )
    //                     }
    //                 </div>
    //             </div>
    //         </div>
    //     </div>
    //     <StatusBar />
    // </div>
  );
};

export default DesktopMainPage;
